import { Middleware } from '@reduxjs/toolkit'
import { Action } from 'redux'
import { HYDRATE_OUTBOX, sendMessage } from './channels/actions'
import { IOutboxEntry } from './channels/offline'
import { SOCKET_STATUS_CHANGED, SocketStatus } from './connection/actions'
import { SIGN_OUT_ACTIONS } from './profile/actions'
import { IRootState } from './reducer'

/**
 * Keeps the AIM outbox alive across reloads and sends what it holds once the network is back.
 *
 * Persisted per user in localStorage — small, and it must survive the app being killed — and
 * removed on sign-out, since it holds message bodies. Flushed oldest first and one at a time,
 * so a conversation's messages arrive in the order they were written; the server's
 * clientMessageId makes a resend of one that had in fact landed a no-op.
 */

export const outboxStorageKey = (userId: string): string => `aim-outbox:${userId}`

const readStoredOutbox = (userId: string): Record<string, IOutboxEntry> | null => {
  try {
    const raw = localStorage.getItem(outboxStorageKey(userId))
    return raw ? (JSON.parse(raw) as Record<string, IOutboxEntry>) : null
  } catch {
    return null
  }
}

/**
 * Apply this tab's change to what is stored, rather than overwriting it: every tab holds its
 * own copy of the outbox, and a tab that never saw another's queued message must not erase it.
 */
const writeOutboxChange = (
  userId: string,
  before: Record<string, IOutboxEntry>,
  after: Record<string, IOutboxEntry>,
) => {
  try {
    const stored = readStoredOutbox(userId) ?? {}
    const merged = { ...stored }
    Object.keys(before)
      .filter((id) => !(id in after))
      .forEach((id) => delete merged[id])
    Object.entries(after)
      .filter(([id, entry]) => before[id] !== entry)
      .forEach(([id, entry]) => {
        merged[id] = entry
      })
    if (Object.keys(merged).length) {
      localStorage.setItem(outboxStorageKey(userId), JSON.stringify(merged))
    } else {
      localStorage.removeItem(outboxStorageKey(userId))
    }
  } catch {
    // Storage full or blocked: the outbox still works for this session.
  }
}

const clearStoredOutbox = (userId: string) => {
  try {
    localStorage.removeItem(outboxStorageKey(userId))
  } catch {
    // Nothing to clear.
  }
}

const currentUserId = (state: IRootState): string | null => state.profile?.user?.id ?? null

const outboxMiddleware = (): Middleware<object, IRootState> => (store) => {
  let hydratedForUserId: string | null = null
  let isFlushing = false

  const flush = async () => {
    if (isFlushing || (typeof navigator !== 'undefined' && !navigator.onLine)) return
    isFlushing = true
    try {
      const queued = Object.values(store.getState().channels.outbox)
        .filter((entry) => entry.status === 'queued')
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      for (const entry of queued) {
        // Discarded, or already resent elsewhere, while an earlier send was in flight.
        if (store.getState().channels.outbox[entry.clientMessageId]?.status !== 'queued') continue
        const send = sendMessage(entry.channelId, entry.data, entry.clientMessageId)
        await (store.dispatch(send as unknown as Action) as unknown as Promise<unknown>)
        // Still unreachable: stop, and let the next reconnect carry on from here.
        if (store.getState().channels.outbox[entry.clientMessageId]?.status === 'queued') break
      }
    } finally {
      isFlushing = false
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => void flush())
  }

  return (next) => (action: { type?: string; data?: unknown }) => {
    const before = store.getState()
    const result = next(action)
    const after = store.getState()

    if (action.type && SIGN_OUT_ACTIONS.includes(action.type)) {
      const leavingUserId = currentUserId(before)
      if (leavingUserId) clearStoredOutbox(leavingUserId)
      hydratedForUserId = null
      return result
    }

    const userId = currentUserId(after)
    if (userId && hydratedForUserId !== userId) {
      hydratedForUserId = userId
      const stored = readStoredOutbox(userId)
      if (stored) store.dispatch({ type: HYDRATE_OUTBOX, data: stored })
      void flush()
    } else if (userId && after.channels.outbox !== before.channels.outbox) {
      writeOutboxChange(userId, before.channels.outbox, after.channels.outbox)
    }

    if (
      action.type === SOCKET_STATUS_CHANGED &&
      (action.data as { status?: SocketStatus })?.status === SocketStatus.Connected
    ) {
      void flush()
    }
    return result
  }
}

export default outboxMiddleware
